import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { FileSpreadsheet, Package, ClipboardList, FileText, Truck, BarChart3 } from 'lucide-react';
import apiClient from '../utils/api';
import { toast } from 'sonner';
import CreateOrderModal from '../components/operator/CreateOrderModal';
import StageCompletionModal from '../components/operator/StageCompletionModal';

// Lazy-loaded tab bundles — each tab's JS only downloads when the user opens it.
// Cuts the initial JS payload of the dashboard by ~60% because Reports / Stock
// / BS Order / Production / Invoice modules are heavy but rarely all needed
// on first paint.
const InvoiceManagement    = lazy(() => import('../components/manager/InvoiceManagement'));
const JobSheetTab          = lazy(() => import('../components/manager/JobSheetTab'));
const BSOrderTab           = lazy(() => import('../components/manager/BSOrderTab'));
const ProductionScheduleTab = lazy(() => import('../components/manager/ProductionScheduleTab'));
const StockTab             = lazy(() => import('../components/manager/StockTab'));
const ReportsTab           = lazy(() => import('../components/manager/ReportsTab'));

const TabFallback = () => (
  <div className="p-8 text-center text-sm text-slate-400">Loading tab…</div>
);

const ManagerDashboard = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => location.state?.tab || 'jobsheet');
  // React to nav dropdown selecting a sub-tab from a different route
  useEffect(() => {
    if (location.state?.tab) setActiveTab(location.state.tab);
  }, [location.state]);
  const [allOrders, setAllOrders] = useState([]);
  const [orderStages, setOrderStages] = useState({});
  const [bsSuppliers, setBsSuppliers] = useState([]);
  const [bsOrders, setBsOrders] = useState([]);
  const [bsCustomers, setBsCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  // Shared month/year — drives the /dashboard-data narrowing. Any tab can
  // update these to refetch. 'all' = no month filter (send everything).
  // filterMonth may be a comma-joined list of 0-based months ("0,2,5") and
  // filterYear a comma-joined list of years ("2025,2026") — multi-select.
  const _now = new Date();
  const [filterMonth, setFilterMonth] = useState(String(_now.getMonth()));  // '0..11', comma list, or 'all'
  const [filterYear, setFilterYear] = useState(String(_now.getFullYear())); // '2026' or comma list

  // Order modal state
  const [showCreateOrder, setShowCreateOrder] = useState(false);
  const [editingOrderId, setEditingOrderId] = useState(null);

  // Stage completion modal state
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedStageName, setSelectedStageName] = useState(null);
  const [selectedStageData, setSelectedStageData] = useState(null);
  const [selectedProgressDate, setSelectedProgressDate] = useState(null);
  const [showStageModal, setShowStageModal] = useState(false);

  const fetchData = useCallback(async (opts = {}) => {
    const { silent = false } = opts;
    try {
      const params = {};
      if (filterMonth !== 'all' && filterMonth !== '' && filterMonth != null) {
        // Multi-select aware: split comma lists, convert 0-based months to 1..12
        const months = String(filterMonth).split(',').map(s => s.trim()).filter(s => s !== '' && s !== 'all').map(s => parseInt(s, 10) + 1);
        const years = String(filterYear).split(',').map(s => s.trim()).filter(Boolean);
        if (months.length && years.length) {
          params.month = months.join(',');
          params.year = years.join(',');
        } else if (years.length) {
          params.year = years.join(',');
        }
      }
      const response = await apiClient.get('/dashboard-data', { params });
      const data = response.data;
      setAllOrders(data.orders || []);
      setBsSuppliers(data.bs_suppliers || []);
      setBsOrders(data.bs_orders || []);
      setBsCustomers(data.customers || []);
      setOrderStages(data.stages || {});
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      if (!silent) toast.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  }, [filterMonth, filterYear]);

  // Silent background refresh used after stage quick-toggles
  const refreshSilently = useCallback(() => fetchData({ silent: true }), [fetchData]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleStageClick = (order, stageName, progressDate = null) => {
    if (order.status === 'cancelled') return;
    const stages = orderStages[order.id] || [];
    const stageData = stages.find(s => s.stage_name === stageName);
    setSelectedOrder(order);
    setSelectedStageName(stageName);
    setSelectedStageData(stageData);
    setSelectedProgressDate(progressDate);
    setShowStageModal(true);
  };

  const handleStageUpdated = () => {
    setShowStageModal(false);
    setSelectedOrder(null);
    setSelectedStageName(null);
    setSelectedStageData(null);
    setSelectedProgressDate(null);
    fetchData();
  };

  const handleCancelOrder = async (orderId, orderName) => {
    if (!window.confirm(`Are you sure you want to cancel order "${orderName}"? This action cannot be undone.`)) return;
    try {
      await apiClient.put(`/orders/${orderId}/cancel`);
      toast.success('Order cancelled successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel order');
    }
  };

  const handleEditOrder = (order) => {
    setEditingOrderId(order.id);
    setShowCreateOrder(true);
  };

  const pendingBsCount = allOrders.filter(order => {
    if (order.status === 'cancelled') return false;
    const stages = orderStages[order.id] || [];
    const bsStage = stages.find(s => s.stage_name === 'Base Shell Procurement');
    return !bsStage || bsStage.status !== 'completed';
  }).length;

  // Note: previously blocked the whole dashboard behind a "Loading dashboard…"
  // screen. That blocks first paint by ~data-fetch-latency + tab-bundle-compile.
  // Instead we render the shell immediately with empty data — each tab already
  // handles empty-state gracefully. `loading` still drives a subtle top indicator.

  return (
    <div data-testid="manager-dashboard" className="min-h-screen bg-slate-50">
      <Navbar />
      {loading && (
        <div className="fixed top-0 left-0 right-0 h-0.5 bg-slate-200 z-50" data-testid="dashboard-loading">
          <div className="h-full bg-slate-600 animate-pulse w-1/3" />
        </div>
      )}
      <div className="p-6 space-y-6">
        <Suspense fallback={<TabFallback />}>
        {activeTab === 'jobsheet' && (
          <JobSheetTab
            allOrders={allOrders}
            bsCustomers={bsCustomers}
            onEditOrder={handleEditOrder}
            onCreateOrder={() => { setEditingOrderId(null); setShowCreateOrder(true); }}
            filterMonth={filterMonth}
            filterYear={filterYear}
            onFilterChange={(m, y) => { setFilterMonth(m); setFilterYear(y); }}
          />
        )}

        {activeTab === 'bsorder' && (
          <BSOrderTab
            allOrders={allOrders}
            bsOrders={bsOrders}
            bsSuppliers={bsSuppliers}
            bsCustomers={bsCustomers}
            orderStages={orderStages}
            onRefresh={fetchData}
            filterMonth={filterMonth}
            filterYear={filterYear}
            onFilterChange={(m, y) => { setFilterMonth(m); setFilterYear(y); }}
          />
        )}

        {activeTab === 'production' && (
          <ProductionScheduleTab
            allOrders={allOrders}
            orderStages={orderStages}
            customers={bsCustomers}
            bsOrders={bsOrders}
            onStageClick={handleStageClick}
            onCancelOrder={handleCancelOrder}
            onRefresh={refreshSilently}
            filterMonth={filterMonth}
            filterYear={filterYear}
            onFilterChange={(m, y) => { setFilterMonth(m); setFilterYear(y); }}
          />
        )}

        {activeTab === 'invoices' && <InvoiceManagement />}
        {activeTab === 'stock' && <StockTab />}
        {activeTab === 'reports' && <ReportsTab />}
        </Suspense>
      </div>

      {showStageModal && selectedOrder && selectedStageName && (
        <StageCompletionModal
          isOpen={showStageModal}
          onClose={() => { setShowStageModal(false); setSelectedOrder(null); setSelectedStageName(null); setSelectedStageData(null); setSelectedProgressDate(null); }}
          order={selectedOrder}
          stageName={selectedStageName}
          stageData={selectedStageData}
          initialProgressDate={selectedProgressDate}
          onSuccess={handleStageUpdated}
        />
      )}

      {showCreateOrder && (
        <CreateOrderModal
          isOpen={showCreateOrder}
          onClose={() => { setShowCreateOrder(false); setEditingOrderId(null); }}
          onSuccess={() => { setShowCreateOrder(false); setEditingOrderId(null); fetchData(); }}
          editingOrderId={editingOrderId}
        />
      )}
    </div>
  );
};

export default ManagerDashboard;
